import { computed } from 'mobx';
import { ContainerableBlock } from '~/models/blocks/containerable-block.model';
import { IServicesBlockResponse } from '~/types/server.types';
import type { InvoiceServiceItemBlock } from '~/models/blocks/invoice-service-item-block.model';
import type { InvoiceBlock } from '~/models/blocks/invoice-block.model';
import type { ServiceItemBlock } from '~/models/blocks/service-item-block.model';
import { TBlock } from '~/types/builder.types';
import type { FlowRevision } from '~/models/flow-revision.model';
import type { RootStore } from '~/stores/root.store';

type ServicesBlockDataForServer = ReturnType<
    ContainerableBlock['getDataForServer']
> &
    Pick<IServicesBlockResponse, 'type'>;

export class ServicesBlock extends ContainerableBlock {
    type: 'services_block';

    constructor(
        block: IServicesBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
    }

    get hoverable() {
        return false;
    }

    get isDeletable() {
        return false;
    }

    isDraggable() {
        return false;
    }

    dndAcceptedTypes() {
        return null;
    }

    get childBlocks() {
        return super.childBlocks as InvoiceServiceItemBlock[];
    }

    @computed
    get shouldExpandOnSelect() {
        return false;
    }

    @computed
    get invoice() {
        return this.parent as InvoiceBlock;
    }

    @computed
    get errors() {
        const {
            builderUiStore: { showErrors, selectedFlow }
        } = this.rootStore;

        if (!showErrors) {
            return {};
        }

        // flow template - no validation needed.
        if (selectedFlow.is_template) {
            return {};
        }

        // flow instance but has no service selection blocks - no need to select services for invoice.
        if (selectedFlow.serviceSelectionBlocks.length > 0) {
            return {};
        }

        // flow instance - validate that a service exists on invoice
        const errors: any = {};
        if (
            this.childBlocks.length === 0 ||
            this.childBlocks.some(
                childBlock => !(childBlock as ServiceItemBlock).errors
            )
        ) {
            errors.hasErrors = true;
        }

        return errors;
    }

    isSomeServiceTaxed(taxNumber: string): boolean {
        return this.allDescendants().some(
            (service: InvoiceServiceItemBlock) => {
                return service.service_item[taxNumber];
            }
        );
    }

    countServiceTaxed(taxNumber: string): number {
        return this.allDescendants().filter(
            (service: InvoiceServiceItemBlock) => {
                return service.service_item[taxNumber];
            }
        ).length;
    }

    canBeParentOf(block: TBlock) {
        return block.type === 'invoice_service_item_block';
    }

    getDataForServer(): ServicesBlockDataForServer {
        return {
            ...super.getDataForServer(),
            type: this.type as 'services_block'
        };
    }
}
